import { Injectable, inject } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { Observable, Subject } from 'rxjs';

/**
 * Options for configuring the delete confirmation dialog.
 */
export interface BdConfirmDeleteOptions {
  /**
   * The name of the specific item being deleted (e.g., "Umsatz Chart").
   * Used in the message: "Bitte bestätigen, ob {itemName} gelöscht werden soll"
   */
  itemName?: string;

  /**
   * The type of item being deleted (e.g., "Chart Teil", "Spalte", "Seite", "Kachel").
   * Used in the header: "{itemType} löschen?"
   */
  itemType?: string;

  /**
   * Custom header text that overrides the default "{itemType} löschen?" pattern.
   */
  customHeader?: string;

  /**
   * Custom message text that overrides the default confirmation message.
   */
  customMessage?: string;

  /**
   * Custom label for the accept button that overrides the default "Ja, löschen".
   * Useful when the confirmed action is not a deletion (e.g. "Ja, Freigabe entfernen").
   */
  customAcceptLabel?: string;
}

/**
 * Service for displaying delete confirmation dialogs using PrimeNG ConfirmDialog.
 *
 * This service provides a consistent way to confirm delete operations across the application.
 * It returns an Observable that emits `true` when the user confirms the deletion,
 * or completes without emitting when the user cancels.
 *
 * @example
 * ```typescript
 * // Simple usage
 * this._confirmDeleteService.confirm({ itemType: 'Spalte' })
 *   .subscribe(() => {
 *     // User confirmed deletion
 *     this.performDelete();
 *   });
 *
 * // With item name for more specific message
 * this._confirmDeleteService.confirm({
 *   itemType: 'Chart Teil',
 *   itemName: 'Umsatz Chart'
 * }).subscribe(() => this.deletePart(index));
 * ```
 */
@Injectable()
export class BdConfirmDeleteService {
  private readonly _confirmationService = inject(ConfirmationService);

  /**
   * Shows a confirmation dialog for delete operations.
   *
   * @param options Configuration options for the dialog
   * @returns Observable that emits `true` when confirmed, or completes without emitting when cancelled
   */
  public confirm(options: BdConfirmDeleteOptions = {}): Observable<boolean> {
    const result$ = new Subject<boolean>();

    const header = this._buildHeader(options);
    const message = this._buildMessage(options);

    this._confirmationService.confirm({
      header,
      message,
      icon: 'pi pi-trash',
      acceptLabel: options.customAcceptLabel ?? 'Ja, löschen',
      acceptButtonStyleClass: 'p-button-danger',
      rejectLabel: 'Abbrechen',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        result$.next(true);
        result$.complete();
      },
      reject: () => {
        result$.complete();
      },
    });

    return result$.asObservable();
  }

  private _buildHeader(options: BdConfirmDeleteOptions): string {
    if (options.customHeader) {
      return options.customHeader;
    }

    if (options.itemType) {
      return `${options.itemType} löschen?`;
    }

    return 'Löschen bestätigen';
  }

  private _buildMessage(options: BdConfirmDeleteOptions): string {
    if (options.customMessage) {
      return options.customMessage;
    }

    if (options.itemName) {
      return `Bitte bestätigen, ob "${options.itemName}" gelöscht werden soll.`;
    }

    if (options.itemType) {
      return `Bitte bestätigen, ob dieses Element gelöscht werden soll.`;
    }

    return 'Möchten Sie dieses Element wirklich löschen?';
  }
}
